/**
 * Per-user chat session persistence.
 *
 * Sessions are stored under a key scoped to the signed-in Guardian userId.
 * Each session keeps messages + optional shared location so returning to a
 * thread restores UI context. Signed-out visitors get no history.
 */

import { safeStorage } from './safeStorage';

const SESSIONS_KEY_PREFIX = 'guardianChatSessions';
const ACTIVE_KEY_PREFIX = 'guardianActiveSession';

function storageKey(userId) {
  return `${SESSIONS_KEY_PREFIX}_${userId}`;
}

function activeKey(userId) {
  return `${ACTIVE_KEY_PREFIX}_${userId}`;
}

/** True when we have a real user id to scope history to. */
export function hasSessionUser(userId) {
  return Boolean(userId);
}

export function createSessionId() {
  return `guardian_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export function deriveSessionTitle(messages) {
  const firstUser = messages.find((m) => m.type === 'user');
  if (!firstUser?.content) return 'New chat';
  const text = firstUser.content.trim();
  return text.length > 42 ? `${text.slice(0, 42)}…` : text;
}

export function loadSessions(userId) {
  if (!hasSessionUser(userId)) return [];
  const raw = safeStorage.get(storageKey(userId));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    // Corrupt saved history: start clean rather than crash (2026-09-25 gap closure).
    console.warn('Ignoring unreadable chat history:', err);
    return [];
  }
}

export function saveSessions(userId, sessions) {
  if (!hasSessionUser(userId)) return;
  // Quota exceeded or storage disabled: safeStorage logs it and the live
  // thread keeps working (2026-09-25 gap closure).
  safeStorage.set(storageKey(userId), JSON.stringify(sessions));
}

/** Removes this user's saved chats from this device (account deletion, P-4). */
export function clearUserSessions(userId) {
  if (!hasSessionUser(userId)) return;
  safeStorage.remove(storageKey(userId));
  safeStorage.remove(activeKey(userId));
}

export function getActiveSessionId(userId) {
  if (!hasSessionUser(userId)) return null;
  return safeStorage.get(activeKey(userId));
}

export function setActiveSessionId(userId, sessionId) {
  if (!hasSessionUser(userId) || !sessionId) return;
  safeStorage.set(activeKey(userId), sessionId);
}

export function upsertSession(userId, session) {
  if (!hasSessionUser(userId)) return [];
  const sessions = loadSessions(userId);
  const idx = sessions.findIndex((s) => s.id === session.id);
  const next = { ...session, updatedAt: Date.now() };
  if (idx >= 0) {
    sessions[idx] = { ...sessions[idx], ...next };
  } else {
    sessions.unshift({ createdAt: Date.now(), ...next });
  }
  sessions.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  saveSessions(userId, sessions);
  return sessions;
}

export function createSession(userId) {
  if (!hasSessionUser(userId)) {
    return {
      id: createSessionId(),
      title: 'New chat',
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
  }
  const id = createSessionId();
  const session = {
    id,
    title: 'New chat',
    messages: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  upsertSession(userId, session);
  setActiveSessionId(userId, id);
  return session;
}

/**
 * Returns the active session for a signed-in user, creating one if needed.
 * Signed-out: returns a blank in-memory session that is never persisted.
 */
export function getOrCreateActiveSession(userId) {
  if (!hasSessionUser(userId)) {
    return {
      id: null,
      title: 'New chat',
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
  }
  const activeId = getActiveSessionId(userId);
  const sessions = loadSessions(userId);
  if (activeId) {
    const existing = sessions.find((s) => s.id === activeId);
    if (existing) return existing;
  }
  return createSession(userId);
}

export function deleteSession(userId, sessionId) {
  if (!hasSessionUser(userId)) return null;
  const sessions = loadSessions(userId).filter((s) => s.id !== sessionId);
  saveSessions(userId, sessions);
  const activeId = getActiveSessionId(userId);
  if (activeId === sessionId) {
    if (sessions.length > 0) {
      setActiveSessionId(userId, sessions[0].id);
      return sessions[0];
    }
    return createSession(userId);
  }
  return sessions.find((s) => s.id === activeId) || null;
}
